import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
    buildCheckoutCancelUrl,
    buildCheckoutSuccessUrl,
    createCheckoutSessionOptions,
} from '@/entities/subscription';
import { usePageRestore } from '@/shared/lib/use-page-restore';
import { useSheetStore } from '@/shared/stores/sheet-store';

const RESUBSCRIBE_SHEET_TYPE = 'subscription/resubscribe';

export function useResubscribe(returnTo: string) {
    const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
    const isPanelOpen = useSheetStore(
        (state) => state.currentSheet?.type === RESUBSCRIBE_SHEET_TYPE,
    );
    const checkout = useMutation(createCheckoutSessionOptions());

    // `isConfirming` holds through isSuccess to cover the gap between the response and the
    // redirect actually leaving the page, so coming back has to clear it explicitly.
    usePageRestore(() => checkout.reset());

    // The sheet host stays mounted across open/close, so a prior selection would
    // otherwise reappear on the next open — reset it whenever the panel is closed.
    useEffect(() => {
        if (!isPanelOpen) {
            setSelectedPlanCode(null);
        }
    }, [isPanelOpen]);

    const selectPlan = (planCode: string) => {
        setSelectedPlanCode(planCode);
    };

    const clearSelection = () => {
        setSelectedPlanCode(null);
    };

    const confirm = () => {
        if (!selectedPlanCode || checkout.isPending || checkout.isSuccess) return;
        checkout.mutate({
            planCode: selectedPlanCode,
            successUrl: buildCheckoutSuccessUrl(returnTo),
            cancelUrl: buildCheckoutCancelUrl(returnTo),
        });
    };

    return {
        selectedPlanCode,
        selectPlan,
        clearSelection,
        confirm,
        isConfirming: checkout.isPending || checkout.isSuccess,
    };
}
